import React, { useRef } from 'react';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { HotelProduct, MapMarker } from '@withjoy/joykit/icons';
import type { LocationInputV2Props, OptionTypeV2 } from './LocationInputV2.types';
import { CloseIcon } from '@shared/components/LocationInput/LocationInput.styles';
import { usePlaceInfo } from '@shared/core/places/usePlaceInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addAction, addError } from '@shared/utils/logger';
import { usePreDefinedGooglePlaces } from '@shared/utils/hooks/usePreDefinedGooglePlaces';
import { useFeatureValue } from '@shared/core/featureFlags';
import { PlacesServiceStatus } from '@shared/core/google-maps/constants';
import { useGoogleMaps } from '@shared/core/google-maps/useGoogleMaps';
import { getTimezoneFromLocation } from '@shared/utils/timezone';

// Not exact but expected hotel types from google map api
const HOTEL_TYPES = new Set(['lodging', 'hotel', 'guest_house', 'hostel', 'motel', 'resort', 'apartment', 'villa']);

export function useDebouncedValue<T>(wait: number = 500) {
  const [value, setValue] = useState<T>();

  const throttleFn = useMemo(() => debounce(setValue, wait), [wait]);

  return {
    setValue: throttleFn,
    value: value,
  };
}

export const useLocationInputV2 = <T extends {}>({
  onLocationChange,
  onLocationOpen,
  onTimezoneDetected,
  includeMapIcon = false,
  includeHotelIcon = false,
  placeholder,
  emptyInputSuggestions,
  placePredictionTypes = ['(cities)'],
  formik,
  defaultValue,
  allowCustomPlace = true,
  componentRestrictions = undefined,
  telemetryPlacesSearched = undefined,
  telemetryPlaceSelected = undefined,
  iconSize,
  onInputChange,
  iconRight: _iconRight,
  isHotel,
  language,
  locationBias,
}: LocationInputV2Props<T>) => {
  const [googleMaps] = useGoogleMaps({ loadOnMount: true });
  const autocompleteService = useMemo(() => (googleMaps ? new googleMaps.places.AutocompleteService() : undefined), [googleMaps]);

  // Track AbortController for timezone detection to prevent race conditions
  const timezoneAbortControllerRef = useRef<AbortController | null>(null);

  const { preDefinedPlacesEnabled, getSearchPreDefinedPlaces, getPreDefinedPlaceById } = usePreDefinedGooglePlaces();
  const venueAddressLocationEnabled = useFeatureValue('venueAddressLocationEnabled').value === 'on';
  const { fetchPlaceInfo } = usePlaceInfo('essentials', 'locationInputV2', undefined, language);

  const [isFetchingPredictions, setIsFetchingPredictions] = useState(false);
  // Log results if searched for place but not selected any.
  const selectedFromAutocompleteRef = useRef<{ isSelected: boolean; input?: string; selectOptions: OptionTypeV2[] }>({ isSelected: true, input: defaultValue, selectOptions: [] });
  // Prevent unwanted autocomplete api call, After select location from autocomplete list. And make api call only after manually change value from input field.
  const [isInputUpdatedFromApi, setIsInputUpdatedFromApi] = useState(false);
  const [selectOptions, setSelectOptions] = useState<OptionTypeV2[]>([]);
  const [autocompleteQueriesMade, setAutocompleteQueriesMade] = useState<number>(0);
  const { value: debouncedValue, setValue } = useDebouncedValue<string>();
  const [inputValue, setInputValue] = useState<string>(defaultValue ?? '');
  const suggestionOptions = useMemo(
    () => (emptyInputSuggestions ?? []).map<OptionTypeV2>(({ id, label, subtitle, iconStart }) => ({ value: `suggestion:${id}`, label, subtitle, iconStart, isHotel: false })),
    [emptyInputSuggestions],
  );
  const isShowingSuggestions = !inputValue.trim() && suggestionOptions.length > 0;
  const iconRight: React.ReactNode = _iconRight ? (
    _iconRight
  ) : inputValue ? (
    <CloseIcon
      size="sm"
      onClick={() => {
        setInputValue('');
        onLocationChange?.(null);
        setSelectOptions([]);
      }}
    />
  ) : (
    // replace search icon fallback with empty fragment
    <></>
  );

  useEffect(() => {
    setInputValue(defaultValue ?? '');
  }, [defaultValue]);

  const iconLeft = includeHotelIcon ? <HotelProduct size={iconSize || 'md'} /> : includeMapIcon ? <MapMarker size={iconSize || 'md'} /> : undefined;

  useEffect(() => {
    if (!autocompleteService || !debouncedValue || debouncedValue.length < 3 || isInputUpdatedFromApi) {
      return;
    }

    const placePredictionsParams: google.maps.places.AutocompletionRequest = {
      input: debouncedValue,
      ...(!venueAddressLocationEnabled && { types: placePredictionTypes }),
      ...(language && { language }),
    };

    // Add location bias if provided
    if (locationBias) {
      placePredictionsParams.locationBias = {
        center: { lat: locationBias.latitude, lng: locationBias.longitude },
        radius: locationBias.radius || 100000, // Default 100km radius
      };
    }

    if (componentRestrictions) {
      placePredictionsParams.componentRestrictions = componentRestrictions;
    }

    setIsFetchingPredictions(true);
    autocompleteService.getPlacePredictions(placePredictionsParams, (places, status) => {
      if (status !== PlacesServiceStatus.OK) {
        if (status !== PlacesServiceStatus.ZERO_RESULTS) {
          addError('LocationAutocompleteFailed', { context: 'LocationInputV2', status });
        }
        setSelectOptions([]);
        setIsFetchingPredictions(false);
        return;
      }

      addAction('LocationAutocomplete', { context: 'LocationInputV2', status, input: debouncedValue, places: places?.map(place => place.description) || [] });
      telemetryPlacesSearched?.(debouncedValue, places?.map(place => place.description) || []);

      const placePredictionTypesSet = new Set(placePredictionTypes);

      const placesPredictions =
        places
          ?.filter(place => {
            // Filter predictions based on placePredictionTypes, but only if venueAddressLocationEnabled.
            if (venueAddressLocationEnabled && placePredictionTypesSet.size) {
              return place.types.some(type => placePredictionTypesSet.has(type));
            }
            return true;
          })
          .map(place => ({
            value: place.place_id,
            label: place.structured_formatting?.main_text || place.description,
            subtitle: place.structured_formatting?.secondary_text || '',
            isHotel: place.types.some(type => HOTEL_TYPES.has(type)),
          })) || [];

      if (preDefinedPlacesEnabled) {
        const results = getSearchPreDefinedPlaces(debouncedValue);
        if (results.length) {
          results.forEach(({ placeId, name, address, placeTypes, shouldOverrideExisting }) => {
            if (!name || !address) return;
            const isHotel = Boolean(placeTypes?.some(type => HOTEL_TYPES.has(type)));
            const placePredictionIndex = placesPredictions.findIndex(prediction => prediction.value === placeId);
            if (placePredictionIndex !== -1) {
              if (shouldOverrideExisting) {
                placesPredictions[placePredictionIndex] = { value: placeId, label: name, subtitle: address, isHotel };
              }
            } else {
              placesPredictions.push({ value: placeId, label: name, subtitle: address, isHotel });
            }
          });
        }
      }
      setSelectOptions(placesPredictions);
      selectedFromAutocompleteRef.current = {
        isSelected: false,
        input: debouncedValue,
        selectOptions: placesPredictions,
      };
      setIsFetchingPredictions(false);
    });
    setAutocompleteQueriesMade(prev => prev + 1);
  }, [debouncedValue, autocompleteService, locationBias, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputValue = (value: string) => {
    formik?.setFieldValue('locationValue', value);
    setValue(value);
    setInputValue(value);
    onInputChange?.(value);
    setIsInputUpdatedFromApi(false);
  };

  const detectTimezone = async (lat?: number, lng?: number): Promise<void> => {
    // Fetch timezone asynchronously if coordinates available
    if (typeof lat === 'number' && typeof lng === 'number' && onTimezoneDetected) {
      timezoneAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      timezoneAbortControllerRef.current = abortController;

      try {
        const timezone = await getTimezoneFromLocation({ lat, lng }, '', abortController.signal);
        if (!abortController.signal.aborted && timezone) {
          onTimezoneDetected(timezone);
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'name' in error && error.name !== 'AbortError') {
          addError(error, { context: 'LocationInputV2', message: 'error getting timezone from location' });
        }
      }
    }
  };

  const handleChange = async (option: OptionTypeV2 | null): Promise<void> => {
    if (!option) {
      return;
    }
    setIsInputUpdatedFromApi(true);
    selectedFromAutocompleteRef.current.isSelected = true;

    const { value, label, subtitle } = option;
    const suggestion = emptyInputSuggestions?.find(({ id }) => value === `suggestion:${id}`);
    if (suggestion) {
      timezoneAbortControllerRef.current?.abort();
      setValue('');
      setInputValue(suggestion.location.displayName);
      setSelectOptions([]);
      formik?.setFieldValue('locationValue', suggestion.location.displayName);
      onLocationChange?.(suggestion.location);
      await detectTimezone(suggestion.location.lat, suggestion.location.lng);
      return;
    }

    const isPredictedPlace = selectOptions.some(v => v.value === value);

    if (!isPredictedPlace) {
      timezoneAbortControllerRef.current?.abort();
      setInputValue(value);
      onLocationChange?.({ placeId: value, displayName: value });
      addAction('LocationAutocompleteRequestMade', { context: 'LocationInputV2', input: debouncedValue, place: '', autocompleteQueriesMade });
      telemetryPlaceSelected?.(debouncedValue || '', '', autocompleteQueriesMade);
      return;
    }

    if (preDefinedPlacesEnabled) {
      const preDefinedPlace = getPreDefinedPlaceById(value);
      if (preDefinedPlace && preDefinedPlace.shouldOverrideExisting) {
        addAction('LocationAutocompleteRequestMade', {
          context: 'LocationInputV2',
          input: debouncedValue,
          place: preDefinedPlace.formattedAddress || preDefinedPlace.address,
          autocompleteQueriesMade,
        });
        telemetryPlaceSelected?.(debouncedValue || '', preDefinedPlace.formattedAddress || preDefinedPlace.address, autocompleteQueriesMade);
        onLocationChange?.({ ...preDefinedPlace, displayName: preDefinedPlace.name });
        timezoneAbortControllerRef.current?.abort();
        return;
      }
    }

    const place = await fetchPlaceInfo(value);
    if (!place) {
      timezoneAbortControllerRef.current?.abort();
      if (preDefinedPlacesEnabled) {
        const preDefinedPlace = getPreDefinedPlaceById(value);
        if (preDefinedPlace) {
          addAction('LocationAutocompleteRequestMade', {
            context: 'LocationInputV2',
            input: debouncedValue,
            place: preDefinedPlace.formattedAddress || preDefinedPlace.address,
            autocompleteQueriesMade,
          });
          telemetryPlaceSelected?.(debouncedValue || '', preDefinedPlace.formattedAddress || preDefinedPlace.address, autocompleteQueriesMade);
          onLocationChange?.({ ...preDefinedPlace, displayName: preDefinedPlace.name });
        }
      }
      addError('LocationDetailsFailed', { context: 'LocationInputV2', message: 'No place found' });
      return;
    }

    const lat = place.location.latitude;
    const lng = place.location.longitude;

    const defaultAddress = { languageCode: '', types: [], longText: '', shortText: '' };
    const findComponent = (type: string) => place.addressComponents.find(component => component.types.includes(type)) ?? defaultAddress;

    const countryComponent = findComponent('country');
    const address2Component = findComponent('administrative_area_level_2') || findComponent('administrative_area_level_3');
    const stateRegionProvinceComponent = findComponent('administrative_area_level_1');
    const postalCodeComponent = findComponent('postal_code');
    const localityComponent = findComponent('locality');

    addAction('LocationAutocompleteRequestMade', { context: 'LocationInputV2', input: debouncedValue, place: place.formattedAddress, autocompleteQueriesMade });
    telemetryPlaceSelected?.(debouncedValue || '', place.formattedAddress || '', autocompleteQueriesMade);

    onLocationChange?.({
      formattedAddress: place.formattedAddress,
      lat,
      lng,
      displayName: label,
      address: subtitle,
      address2: address2Component.longText,
      placeTypes: place.types,
      placeId: place.id,
      country: countryComponent.longText,
      countryCode: countryComponent.shortText || undefined,
      stateRegionProvince: stateRegionProvinceComponent.longText,
      city: localityComponent.longText,
      postalCode: postalCodeComponent.longText,
    });

    await detectTimezone(lat, lng);
  };

  // Prevent unwanted autocomplete api call, when edit form with pre-filled value. And make api call, when click on autocomplete input field.
  const handleOnLocationOpen = useEventCallback(() => {
    if (inputValue) {
      setValue(inputValue);
    }
    onLocationOpen?.();
  });

  const handleOnLocationClose = useEventCallback(() => {
    if (!selectedFromAutocompleteRef.current.isSelected) {
      if (defaultValue !== inputValue) {
        handleInputValue(inputValue || '');
        setValue(inputValue);
        setInputValue(inputValue || '');
        onInputChange?.(inputValue || '');
        setIsInputUpdatedFromApi(false);
      }
      addAction('LocationAutocompleteNotSelected', {
        context: 'LocationInputV2',
        input: selectedFromAutocompleteRef.current.input,
        places: selectedFromAutocompleteRef.current.selectOptions.map(place => place.label),
      });
    }
  });

  // Cleanup: abort pending timezone request on unmount
  useEffect(() => {
    return () => {
      timezoneAbortControllerRef.current?.abort();
    };
  }, []);

  return {
    isShowingSuggestions,
    options: isShowingSuggestions
      ? suggestionOptions
      : [...(allowCustomPlace && inputValue ? [{ label: `Use "${inputValue}"`, subtitle: '', value: inputValue, isHotel: Boolean(isHotel) }] : []), ...selectOptions],
    handleChange,
    handleInputValue,
    onLocationOpen: handleOnLocationOpen,
    onLocationClose: handleOnLocationClose,
    iconRight,
    iconLeft,
    inputValue,
    placeholder,
    placePredictionTypes,
    isFetchingPredictions,
  };
};
